﻿import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CreditCard } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CreatePaymentRequest, Uuid, VirtualDevice } from "@tilt/pos-sdk";

import { getPosClient } from "../../lib/api";
import { formatCents } from "../../lib/format";
import { initializePassageV2Form, type PassageInstance } from "../../lib/valor-passage";
import { Button } from "../ui/Button";
import { Level3Form, type Level3FormHandle } from "./Level3Form";

interface VirtualTerminalPanelProps {
  readonly locationId: Uuid;
  readonly amountCents: number;
  readonly currency: string;
  readonly idempotencyKey: string;
  readonly cardholderNameDefault?: string;
  readonly customerPhoneDefault?: string;
  readonly submitting: boolean;
  readonly onSubmit: (body: CreatePaymentRequest) => void;
  readonly onCancel: () => void;
}

export function VirtualTerminalPanel({
  locationId,
  amountCents,
  currency,
  idempotencyKey,
  cardholderNameDefault,
  customerPhoneDefault,
  submitting,
  onSubmit,
  onCancel,
}: VirtualTerminalPanelProps) {
  const { t } = useTranslation();

  const [vendorError, setVendorError] = useState<string | null>(null);
  const [l3Valid, setL3Valid] = useState(false);
  const [l3Error, setL3Error] = useState<string | null>(null);
  const [selectedDeviceRef, setSelectedDeviceRef] = useState<string | null>(null);
  const [tokenFetching, setTokenFetching] = useState(false);

  const hostRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<PassageInstance | null>(null);
  const l3Ref = useRef<Level3FormHandle>(null);
  const selectedDeviceRefRef = useRef<string | null>(null);
  // Synchronous guard - prevents a second mintIframeToken call if the
  // button is clicked again before setTokenFetching(true) propagates.
  const payingRef = useRef(false);
  // Cleanup for the form-submit blocker installed before openLightbox().
  const unblockFormNavRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    selectedDeviceRefRef.current = selectedDeviceRef;
  }, [selectedDeviceRef]);

  // Keep these stable so the Pay handler always reads current values without
  // re-creating the closure on every render.
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);
  const amountCentsRef = useRef(amountCents);
  useEffect(() => {
    amountCentsRef.current = amountCents;
  }, [amountCents]);

  const devicesQuery = useQuery({
    queryKey: ["virtual-devices", locationId],
    queryFn: () => getPosClient().listVirtualDevices(locationId),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const devices: ReadonlyArray<VirtualDevice> = useMemo(
    () => devicesQuery.data?.devices ?? [],
    [devicesQuery.data],
  );

  // Auto-select when data loads: prefer the binding-default device, then
  // first in list. If the previous selection is still valid, keep it.
  useEffect(() => {
    if (devices.length === 0) return;
    setSelectedDeviceRef((prev) => {
      if (prev && devices.some((d) => d.device_ref === prev)) return prev;
      const defaultDevice = devices.find((d) => d.is_default);
      return (defaultDevice ?? devices[0]).device_ref;
    });
  }, [devices]);

  // Destroy any live Passage instance on unmount and remove the form-nav blocker.
  useEffect(() => {
    return () => {
      unblockFormNavRef.current?.();
      unblockFormNavRef.current = null;
      instanceRef.current?.destroy?.();
      instanceRef.current = null;
    };
  }, []);

  const handleL3Change = (balanced: boolean) => {
    setL3Valid(balanced);
    setL3Error(null);
  };

  // Pay is only enabled once: L3 form is valid, devices have loaded, a
  // device is selected (when required), and no in-flight operation is
  // running. The token is fetched lazily on click - not eagerly on mount -
  // so Valor is not billed for every page navigation.
  const devicesLoaded = devicesQuery.isSuccess || devicesQuery.isError;
  const deviceReady = devicesLoaded && (devices.length < 2 || !!selectedDeviceRef);
  const canPay = l3Valid && deviceReady && !submitting && !tokenFetching;

  const handlePay = async () => {
    if (payingRef.current) return;
    payingRef.current = true;
    if (!l3Ref.current?.validate()) {
      setL3Error(t("sale.virtual.l3Invalid"));
      payingRef.current = false;
      return;
    }
    setL3Error(null);
    setVendorError(null);
    setTokenFetching(true);

    // Restore the real HTMLFormElement.prototype.submit BEFORE capturing
    // origSubmit below. Without this, origSubmit on the second payment
    // captures the no-op patch from the first payment (which was never
    // restored because onTokenReceived intentionally skips the restore).
    // That causes the real submit to be permanently lost, and Passage's
    // deferred form.submit() eventually navigates the browser to passage-ack
    // as a full page load, aborting the payment fetch.
    unblockFormNavRef.current?.();
    unblockFormNavRef.current = null;
    // Clean up any stale instance from a previous attempt.
    instanceRef.current?.destroy?.();
    instanceRef.current = null;
    if (hostRef.current) hostRef.current.innerHTML = "";

    // Capture the device ref used for this token so onTokenReceived sends
    // the same ref - prevents a mismatch if the picker changes mid-lightbox.
    const capturedDeviceRef = selectedDeviceRef;

    try {
      const tokenData = await getPosClient().mintIframeToken(locationId, {
        payment_subtype: "virtual",
        ...(capturedDeviceRef ? { device_id: capturedDeviceRef } : {}),
      });

      // Passage lightbox requires formAction to initialize properly - without
      // it, openLightbox() is a no-op. We point it at our passage-ack endpoint
      // which returns {"ok":true} JSON. Passage POSTs there after tokenization,
      // gets a parseable response, and doesn't fall back to window.location
      // navigation (which happens when it receives the SPA's index.html).
      // The real payment is handled via onTokenReceived → createPayment.
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
      const { instance } = await initializePassageV2Form({
        hostElementId: "valor-passage-host",
        clientToken: tokenData.token,
        epi: tokenData.epi,
        isDemo: tokenData.is_demo,
        formAction: `${apiBase.replace(/\/$/, "")}/pos/v1/passage-ack`,
        defaults: {
          ...(cardholderNameDefault ? { cardholderName: cardholderNameDefault } : {}),
          ...(customerPhoneDefault ? { phone: customerPhoneDefault } : {}),
        },
        onTokenReceived(cardToken) {
          // Keep the form.submit patch active -- Passage navigates to formAction
          // AFTER calling onTokenReceived, so restoring here would un-patch too early.
          // The patch is removed on component unmount (after the sale completes).
          const l3 = l3Ref.current?.getSanitized();
          const devRef = capturedDeviceRef;
          const body: CreatePaymentRequest = {
            payment_type: "card",
            payment_subtype: "virtual",
            amount_cents: amountCentsRef.current,
            iframe_token: cardToken,
            idempotency_key: idempotencyKey,
            ...(l3 ? { level3: l3 } : {}),
            ...(devRef ? { device_id: devRef } : {}),
          };
          onSubmitRef.current(body);
        },
        onError(err) {
          unblockFormNavRef.current?.();
          unblockFormNavRef.current = null;
          setVendorError(err);
        },
        onSessionExpired() {
          unblockFormNavRef.current?.();
          unblockFormNavRef.current = null;
          // Valor session expired while lightbox was open - surface an error
          // and let the cashier click Pay again to get a fresh token.
          setVendorError(t("sale.virtual.tokenError"));
        },
      });

      instanceRef.current = instance;
      setTokenFetching(false);
      payingRef.current = false;

      // Passage calls form.submit() directly (programmatic submit), which
      // bypasses the 'submit' event entirely -- preventDefault() never fires.
      // Patch HTMLFormElement.prototype.submit to a no-op so the hidden form
      // cannot navigate the browser. Restored in every exit path below.
      const origSubmit = HTMLFormElement.prototype.submit;
      HTMLFormElement.prototype.submit = function () {
        /* blocked during Passage lightbox */
      };
      unblockFormNavRef.current = () => {
        HTMLFormElement.prototype.submit = origSubmit;
      };

      if (typeof instance.openLightbox === "function") {
        instance.openLightbox();
      } else {
        hostRef.current?.querySelector("button")?.click();
      }
    } catch {
      unblockFormNavRef.current?.();
      unblockFormNavRef.current = null;
      setVendorError(t("sale.virtual.tokenError"));
      setTokenFetching(false);
      payingRef.current = false;
    }
  };

  return (
    <div
      className="space-y-4 rounded-xl border border-line bg-card p-4 shadow-elev-1"
      role="dialog"
      aria-label={t("sale.virtual.panelLabel")}
    >
      <div className="flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-brand-primary" aria-hidden />
        <span className="text-sm font-semibold text-strong">{t("sale.virtual.title")}</span>
        <span className="ml-auto text-sm font-medium text-muted">
          {formatCents(amountCents, currency)}
        </span>
      </div>

      {/* Vendor / token error banner */}
      {vendorError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-2.5 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{vendorError}</span>
          <button
            type="button"
            onClick={() => setVendorError(null)}
            className="ml-auto flex items-center gap-1 text-xs underline"
          >
            {t("sale.virtual.dismiss")}
          </button>
        </div>
      )}

      {/* Off-screen mount point - keeps the SDK's trigger button accessible
          for .click() without appearing in the visible layout. */}
      <div
        id="valor-passage-host"
        ref={hostRef}
        className="absolute -left-[9999px]"
        aria-hidden="true"
      />

      {/* Virtual device picker - shown only when 2+ Soft POS devices are
          bound at this location. Single-device locations auto-select
          silently to avoid UI clutter. */}
      {devices.length >= 2 && (
        <div className="flex flex-col gap-1">
          <label htmlFor="virtual-device-picker" className="text-xs font-medium text-muted">
            {t("sale.virtual.devicePicker")}
          </label>
          <select
            id="virtual-device-picker"
            value={selectedDeviceRef ?? ""}
            onChange={(e) => setSelectedDeviceRef(e.target.value || null)}
            className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary"
          >
            <option value="">{t("sale.virtual.devicePickerDefault")}</option>
            {devices.map((d) => (
              <option key={d.device_ref} value={d.device_ref}>
                {d.label ? `${d.label} (${d.device_ref})` : d.device_ref}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Level 3 form - always visible so the cashier can fill it before
          clicking Pay. The token is only fetched after this is valid. */}
      <Level3Form
        ref={l3Ref}
        amountCents={amountCents}
        onChange={handleL3Change}
        className="border-0 p-0 shadow-none"
      />

      {/* L3 validation error */}
      {l3Error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-2.5 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{l3Error}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={submitting || tokenFetching}
          className="flex-1"
        >
          {t("sale.virtual.cancel")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void handlePay()}
          loading={submitting || tokenFetching}
          disabled={!canPay}
          className="flex-1"
        >
          {tokenFetching
            ? t("sale.virtual.loadingForm")
            : t("sale.virtual.pay", { amount: formatCents(amountCents, currency) })}
        </Button>
      </div>
    </div>
  );
}
